import { useEffect, useId, useRef, type ReactNode } from 'react'
import { useModalTelemetry, type ModalName } from '@/lib/modalTelemetry'
import s from '@/shared/components/Modal.module.css'

interface Props {
  /**
   * Which dialog this is, for telemetry. REQUIRED, and a literal from the `ModalName` union rather
   * than anything derived from `title`: titles here interpolate token labels, workspace keys,
   * module names and org slugs, none of which may reach telemetry. See `lib/modalTelemetry.ts`.
   */
  track: ModalName
  title: string
  subtitle?: string
  kicker?: ReactNode
  onClose: () => void
  children: ReactNode
  maxWidth?: number
  footer?: ReactNode
  dismissable?: boolean
  /**
   * Let the panel take its full allowance (90vh) instead of hugging its content, and lay the
   * body out as a flex column. For dialogs built around something that should absorb the
   * leftover space - a code editor, a log view - so it grows with the window rather than
   * pushing the footer actions below the fold.
   */
  fillHeight?: boolean
}

export function Modal({ track, title, subtitle, kicker, onClose, children, maxWidth = 540, footer, dismissable = true, fillHeight = false }: Props) {
  const ref = useRef<HTMLDialogElement>(null)
  const titleId = useId()

  // Instrumented here rather than at each trigger, because mount/unmount is the only place that
  // sees every open and every close - including Escape, a backdrop click, and an unmount while the
  // dialog is still open. Its own effect, above the showModal() one, so an open is recorded even if
  // the native dialog call throws.
  useModalTelemetry(track)

  // Native <dialog> promotes itself to the top-layer, so modal-over-modal stacks
  // in showModal() order with no z-index plumbing. Call sites conditionally render
  // the Modal, so mount === open and unmount === close.
  useEffect(() => {
    const dialog = ref.current
    dialog?.showModal()
    // Mark the document so the topbar's live backdrop-filter blur can be
    // suppressed while a modal is open: it sits inside the region the dialog's
    // own ::backdrop blur samples, so leaving it active makes the open frame
    // blur a viewport that itself contains a second live blur (extra INP on
    // open). Ref-count via data-modal-count so stacked modals restore correctly.
    const body = document.body
    const next = (Number(body.dataset.modalCount) || 0) + 1
    body.dataset.modalCount = String(next)
    return () => {
      dialog?.close()
      const remaining = (Number(body.dataset.modalCount) || 1) - 1
      if (remaining > 0) body.dataset.modalCount = String(remaining)
      else delete body.dataset.modalCount
    }
  }, [])

  return (
    <dialog
      ref={ref}
      className={s.dialog}
      aria-labelledby={titleId}
      // Escape fires `cancel`; preventDefault so the browser doesn't close the
      // dialog out from under React - we drive open/close via mount instead.
      //
      // The target check is what keeps a stacked modal honest: React routes this event through
      // the component tree, so a NESTED dialog's Escape also reaches the handler of the modal it
      // was opened from, and both would close on one keypress. Only the dialog the event
      // actually targets may act on it - the inner one has its own handler for its own Escape.
      onCancel={(e) => {
        if (e.target !== ref.current) return
        e.preventDefault()
        if (dismissable) onClose()
      }}
      // The <dialog> fills the viewport with no padding; the visible panel is the
      // inner div. A click landing on the dialog itself is therefore a backdrop click.
      onClick={(e) => { if (dismissable && e.target === ref.current) onClose() }}
    >
      <div className={`${s.panel}${fillHeight ? ` ${s.panelFill}` : ''}`} style={{ maxWidth }}>
        <div className={s.header}>
          <div>
            {kicker && <div className={s.kicker}>{kicker}</div>}
            {!kicker && subtitle && <div className={s.subtitle}>{subtitle}</div>}
            <h2 id={titleId} className={s.title}>{title}</h2>
          </div>
          {dismissable && (
            // type="button" is load-bearing: a modal opened from inside a <form> (the policy
            // editor opens two) renders its <dialog> as a DESCENDANT of that form, and form
            // ownership follows DOM ancestry, not the top layer. Without a type this button
            // defaults to submit, so closing the dialog also submitted the form behind it.
            <button type="button" className={s.closeBtn} onClick={onClose} aria-label="Close">✕</button>
          )}
        </div>

        <div className={`${s.body}${fillHeight ? ` ${s.bodyFill}` : ''}`}>
          {children}
        </div>

        {footer && (
          <div className={s.footer}>
            {footer}
          </div>
        )}
      </div>
    </dialog>
  )
}
